import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';

interface FeedItem {
  id: string;
  title: string;
  description: string;
  pubDate: string;
}

const RSS_URL = '/rss/rss_feed.xml';

const parseFeed = (xml: Document): FeedItem[] => {
  const items = Array.from(xml.querySelectorAll('item'));
  return items.map(item => {
    const link = item.getElementsByTagName('link')[0]?.textContent ?? '';
    const id = link.split('/').pop()?.replace('.html', '') ?? '';
    const title = item.querySelector('title')?.textContent ?? '';
    const description = item.querySelector('description')?.textContent ?? '';
    const pubDate = item.querySelector('pubDate')?.textContent ?? '';
    return { id, title, description, pubDate };
  }).filter(item => item.id)
    .sort((a, b) => new Date(b.pubDate).getTime() - new Date(a.pubDate).getTime());
};

const formatDate = (pubDate: string): string => {
  const d = new Date(pubDate);
  if (isNaN(d.getTime())) return pubDate;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

export default function KomachiReader() {
  const [items, setItems] = useState<FeedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(RSS_URL)
      .then(r => r.text())
      .then(text => {
        const xml = new DOMParser().parseFromString(text, 'text/xml');
        setItems(parseFeed(xml));
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#09090b]">
      <Helmet>
        <title>Komachi Reader</title>
        <meta name="description" content="Read Komachi forum threads" />
      </Helmet>

      <div className="max-w-2xl mx-auto px-6 py-12">
        <Link to="/" className="text-zinc-600 hover:text-zinc-400 text-sm transition-colors duration-150">← back</Link>

        <p className="text-zinc-600 text-xs tracking-widest uppercase mt-8 mb-6">komachi reader</p>

        {loading && (
          <div className="flex justify-center py-12">
            <div className="w-5 h-5 border-2 border-t-zinc-400 border-zinc-700 rounded-full animate-spin" />
          </div>
        )}

        {error && (
          <p className="text-zinc-600 text-sm">failed to load feed</p>
        )}

        <div className="flex flex-col gap-3">
          {items.map(item => (
            <button
              key={item.id}
              onClick={() => navigate(`/komachi/${item.id}`)}
              className="text-left w-full bg-zinc-900 border border-zinc-800 rounded-lg px-5 py-4 hover:border-zinc-600 transition-colors duration-150"
            >
              <p className="text-zinc-100 text-base mb-1">{item.title}</p>
              <p className="text-zinc-500 text-sm mb-2 overflow-hidden" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{item.description}</p>
              <p className="text-zinc-700 text-xs">{formatDate(item.pubDate)}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
